import React, { useState, useRef, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import { COLOURS } from "../shared";

type DropdownOption = string | { label: string; value: string };

interface DropdownListOrTextInputProps {
  id: string;
  value: string;
  options: DropdownOption[];
  placeholder?: string;
  maxLength?: number;
  onChange: (value: string) => void;
  onSelect: (value: string, isCustom: boolean) => void;
  mode?: "import" | "export";
}

const Input = styled.input`
  height: 3.2rem;
  padding: 0.6rem 0.8rem;
  font-size: ${(props) => props.theme.fontSize.base};
  border: 1px solid ${COLOURS.modalRadioButtonInputBorder};
  border-radius: 0.6rem;
  width: 100%;
  outline: none;

  &:focus {
    border-color: ${COLOURS.modalRadioCheckedButtonInputBorder};
    background-color: ${COLOURS.modalRadioCheckedButtonInputBackground};
  }

  &:disabled {
    background-color: #f9f9f9;
    color: #aaa;
  }
`;

const DropdownList = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  background: white;
  border: 1px solid #ddd;
  border-radius: 0.6rem;
  max-height: 15rem;
  overflow-y: auto;
  box-shadow: 0 8px 16px rgba(0, 0, 0, 0.1);
  z-index: 10;
`;

const DropdownItem = styled.div`
  padding: 0rem 1rem;
  font-size: ${(props) => props.theme.fontSize.base};
  cursor: pointer;

  &:hover {
    background-color: ${COLOURS.modalRadioCheckedButtonInputBackground};
  }
`;

export const DropdownListOrTextInput: React.FC<
  DropdownListOrTextInputProps
> = ({
  id,
  value,
  options,
  placeholder,
  maxLength,
  onChange,
  onSelect,
  mode,
}) => {
  const dropdownRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [highlighted, setHighlighted] = useState<number>(-1);

  // scroll into view whenever highlighted changes
  useEffect(() => {
    if (highlighted < 0 || !listRef.current) return;
    const items = Array.from(
      listRef.current.querySelectorAll<HTMLElement>("[data-item]"),
    );
    items[highlighted]?.scrollIntoView({ block: "nearest" });
  }, [highlighted]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const filteredOptions = useMemo(() => {
    const lower = value.toLowerCase();
    return options.filter((opt) => {
      const label = typeof opt === "string" ? opt : opt.label;
      return label.toLowerCase().includes(lower);
    });
  }, [value, options]);

  const openDropdown = () => {
    setIsDropdownOpen(true);
    setHighlighted(-1);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isDropdownOpen) return;

    switch (e.key) {
      case "ArrowDown":
        e.preventDefault();
        setHighlighted((i) => {
          const next = i + 1;
          const upperLimit =
            filteredOptions.length > 0
              ? filteredOptions.length - 1
              : value.trim()
                ? 0 // custom option
                : -1;
          return Math.min(next, upperLimit);
        });
        break;

      case "ArrowUp":
        e.preventDefault();
        setHighlighted((i) => {
          const lowerLimit = 0;
          return Math.max(i - 1, lowerLimit);
        });
        break;

      case "Enter":
        e.preventDefault();
        if (highlighted >= 0 && highlighted < filteredOptions.length) {
          const opt = filteredOptions[highlighted];
          const val = typeof opt === "string" ? opt : opt.value;
          onSelect(val, false);
          setIsDropdownOpen(false);
          inputRef.current?.blur();
        } else if (filteredOptions.length === 0 && value.trim()) {
          onSelect(value.trim(), true);
          setIsDropdownOpen(false);
          inputRef.current?.blur();
        }
        break;
    }
  };

  return (
    <div ref={dropdownRef} style={{ position: "relative" }}>
      <Input
        ref={inputRef}
        id={id}
        type="text"
        placeholder={placeholder}
        value={value}
        maxLength={maxLength}
        onChange={(e) => {
          onChange(e.target.value);
          openDropdown();
        }}
        onFocus={openDropdown}
        onKeyDown={handleKeyDown}
      />

      {isDropdownOpen && (
        <DropdownList ref={listRef}>
          {filteredOptions.map((option, idx) => {
            const label = typeof option === "string" ? option : option.label;
            const actualValue =
              typeof option === "string" ? option : option.value;
            const isActive = idx === highlighted;

            return (
              <DropdownItem
                data-item
                key={actualValue}
                onClick={() => {
                  onSelect(actualValue, false);
                  setIsDropdownOpen(false);
                  inputRef.current?.blur();
                }}
                onMouseEnter={() => setHighlighted(idx)}
                style={{
                  backgroundColor: isActive
                    ? COLOURS.modalRadioCheckedButtonInputBackground
                    : undefined,
                }}
              >
                {label}
              </DropdownItem>
            );
          })}

          {filteredOptions.length === 0 && value.trim() && (
            <DropdownItem
              data-item
              onClick={() => {
                onSelect(value.trim(), true);
                setIsDropdownOpen(false);
                inputRef.current?.blur();
              }}
              onMouseEnter={() => setHighlighted(0)}
              style={{
                backgroundColor:
                  highlighted === 0
                    ? COLOURS.modalRadioCheckedButtonInputBackground
                    : undefined,
              }}
            >
              {mode === "import"
                ? `Can't find your system, use "${value.trim()}"?`
                : `Use "${value.trim()}"?`}
            </DropdownItem>
          )}
        </DropdownList>
      )}
    </div>
  );
};
